import { useEffect } from 'react'
import { useEnvironment } from './environment.ts'

/**
 * The product name, as the masthead and the tab both say it.
 *
 * **Tasks4Staff, not Neuer Standard.** Neuer Standard is the cleaning company that will be
 * this platform's first tenant; naming the product after one of its customers is the
 * mistake the 2026-08-16 rename existed to correct (`docs/DECISIONS.md`, "Renamed to
 * Tasks4Staff"). That rename was scoped to infrastructure and deliberately left prose
 * alone — which left this constant, the one piece of prose a human reads on every single
 * page of this application, still saying the old name.
 *
 * `index.html` carries the same string for the tab title before React mounts, and
 * `document-title.test.ts` asserts the two agree. One name in two files is exactly how
 * this went stale; that test is what stops it happening again.
 *
 * **"Tasks4Staff Admin" since `feat-product-logo-favicon`** (the owner's own wording for the
 * title format, "Tenants · Tasks4Staff Admin"); it was "Tasks4Staff platform admin".
 */
export const PRODUCT_NAME = 'Tasks4Staff Admin'

/**
 * The tab's title: what the page shows, the product, and **the environment**.
 *
 * `parts` runs from the most specific to the least — `['Branding', 'Alpenglanz',
 * 'Tenants']` for a tenant's Branding page — so the tab strip, which truncates from the
 * right, keeps the part that tells two tabs apart (`docs/ux/PLATFORM-ADMIN.md` §5.1,
 * "Page titles"). A part that is `undefined` or blank is left out: that is how a detail
 * page falls back to its section name while its item is loading or was not found.
 *
 * §5.1 puts the environment in the page title as well as in the masthead, because a tab
 * strip full of identical tabs is otherwise unreadable — and telling a pre-prod tab from a
 * prod one is the whole point of the label.
 */
export function pageTitle(parts: readonly (string | undefined)[], environment: string): string {
  return [...parts, PRODUCT_NAME, environment]
    .filter((part): part is string => part !== undefined && part.trim() !== '')
    .join(' · ')
}

export function useDocumentTitle(...parts: readonly (string | undefined)[]): void {
  const environment = useEnvironment()
  const title = pageTitle(parts, environment.label)

  useEffect(() => {
    document.title = title
  }, [title])
}
